import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { AddCircleOutline, DeleteOutline } from '@material-ui/icons'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  highlightRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  highlightFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flex: 1,
  },
  highlightTopRow: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  iconField: {
    flex: 1,
    maxWidth: 160,
  },
  titleField: {
    flex: 2,
  },
  deleteButton: {
    marginTop: theme.spacing(0.5),
  },
  addButton: {
    alignSelf: 'flex-start',
  },
}))

const BlurField = ({ value, onCommit, ...props }) => {
  const [local, setLocal] = useState(value || '')
  useEffect(() => { setLocal(value || '') }, [value])
  return (
    <TextField
      {...props}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value || '')) onCommit(local) }}
    />
  )
}

const AboutEditor = ({ data, onChange }) => {
  const classes = useStyles()
  const highlights = data.highlights || []

  const handleHighlightChange = (index, field, value) => {
    const updated = highlights.map((hl, i) =>
      i === index ? { ...hl, [field]: value } : hl
    )
    onChange('highlights', updated)
  }

  const handleAddHighlight = () => {
    onChange('highlights', [
      ...highlights,
      { icon: '', title: '', description: '' },
    ])
  }

  const handleRemoveHighlight = (index) => {
    onChange(
      'highlights',
      highlights.filter((_, i) => i !== index)
    )
  }

  return (
    <Box className={classes.root}>
      <BlurField
        label="Headline"
        value={data.headline || ''}
        onCommit={(v) => onChange('headline', v)}
        size="small"
        variant="outlined"
        fullWidth
      />

      <BlurField
        label="Body"
        value={data.body || ''}
        onCommit={(v) => onChange('body', v)}
        size="small"
        variant="outlined"
        fullWidth
        multiline
        rows={4}
      />

      <Typography variant="subtitle2">Highlights</Typography>

      {highlights.map((highlight, index) => (
        <Box key={index} className={classes.highlightRow}>
          <Box className={classes.highlightFields}>
            <Box className={classes.highlightTopRow}>
              <BlurField
                label="Icon"
                value={highlight.icon || ''}
                onCommit={(v) =>
                  handleHighlightChange(index, 'icon', v)
                }
                size="small"
                variant="outlined"
                fullWidth
                className={classes.iconField}
              />
              <BlurField
                label="Title"
                value={highlight.title || ''}
                onCommit={(v) =>
                  handleHighlightChange(index, 'title', v)
                }
                size="small"
                variant="outlined"
                fullWidth
                className={classes.titleField}
              />
            </Box>
            <BlurField
              label="Description"
              value={highlight.description || ''}
              onCommit={(v) =>
                handleHighlightChange(index, 'description', v)
              }
              size="small"
              variant="outlined"
              fullWidth
            />
          </Box>
          <IconButton
            size="small"
            onClick={() => handleRemoveHighlight(index)}
            className={classes.deleteButton}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button
        startIcon={<AddCircleOutline />}
        onClick={handleAddHighlight}
        size="small"
        color="primary"
        className={classes.addButton}
      >
        Add Highlight
      </Button>
    </Box>
  )
}

export default AboutEditor
